import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

const GET_TUTOR_DATA = gql`
  query GetTutorData {
    getActiveTutorLesson {
      id
      title
      isActive
      messages {
        id
        sender
        content
        createdAt
      }
    }
    getTutorLessons {
      id
      title
      summary
      isActive
      createdAt
      messages {
        id
        sender
        content
        createdAt
      }
    }
    getTutorProgress {
      id
      statement
      updatedAt
    }
  }
`;

const START_LESSON = gql`
  mutation StartTutorLesson($title: String!) {
    startTutorLesson(title: $title) {
      id
      title
      isActive
      messages {
        id
        sender
        content
        createdAt
      }
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendTutorMessage($lessonId: ID!, $content: String!) {
    sendTutorMessage(lessonId: $lessonId, content: $content) {
      id
      sender
      content
      createdAt
    }
  }
`;

const END_LESSON = gql`
  mutation EndTutorLesson($lessonId: ID!) {
    endTutorLesson(lessonId: $lessonId) {
      id
      summary
      isActive
    }
  }
`;

export const useSvenTutor = () => {
  const { data, loading, error, refetch } = useQuery(GET_TUTOR_DATA);
  const [startLessonMut] = useMutation(START_LESSON);
  const [sendMessageMut] = useMutation(SEND_MESSAGE);
  const [endLessonMut] = useMutation(END_LESSON);

  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedPastLessonId, setSelectedPastLessonId] = useState<string | null>(null);
  const [showEndLessonDialog, setShowEndLessonDialog] = useState(false);
  const [startStatus, setStartStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [endStatus, setEndStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const activeLesson = data?.getActiveTutorLesson;
  const pastLessons = data?.getTutorLessons || [];
  const tutorProgress = data?.getTutorProgress;

  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeLesson?.messages]);

  const handleStartLesson = (title: string) => {
    const lessonTitle = title.trim() || `Swedish Lesson - ${new Date().toLocaleDateString()}`;
    setStartStatus('saving');

    startLessonMut({
      variables: { title: lessonTitle },
      refetchQueries: ['GetTutorData'],
    }).then(() => {
      setStartStatus('saved');
      setSelectedPastLessonId(null);
    }).catch((err) => {
      console.error('Error starting tutor lesson:', err);
      setStartStatus('error');
    });
  };

  const handleSendMessage = (textToSend: string) => {
    if (!textToSend.trim() || !activeLesson || isSending) return;

    setIsSending(true);
    setInputText('');

    sendMessageMut({
      variables: {
        lessonId: activeLesson.id,
        content: textToSend.trim(),
      },
      refetchQueries: ['GetTutorData'],
    }).catch((err) => {
      console.error('Error sending tutor message:', err);
    }).finally(() => {
      setIsSending(false);
    });
  };

  const handleEndLesson = () => {
    if (!activeLesson) return;
    setShowEndLessonDialog(true);
  };

  const handleConfirmEndLesson = () => {
    if (!activeLesson) return;
    setShowEndLessonDialog(false);
    setEndStatus('saving');

    endLessonMut({
      variables: { lessonId: activeLesson.id },
      refetchQueries: ['GetTutorData'],
    }).then(() => {
      setEndStatus('saved');
    }).catch((err) => {
      console.error('Error ending tutor lesson:', err);
      setEndStatus('error');
    });
  };

  const selectedPastLesson = pastLessons.find((l: any) => l.id === selectedPastLessonId);

  return {
    data,
    loading,
    error,
    refetch,
    activeLesson,
    pastLessons,
    tutorProgress,
    selectedPastLesson,
    inputText,
    setInputText,
    isSending,
    selectedPastLessonId,
    setSelectedPastLessonId,
    showEndLessonDialog,
    setShowEndLessonDialog,
    startStatus,
    endStatus,
    messagesEndRef,
    handleStartLesson,
    handleSendMessage,
    handleEndLesson,
    handleConfirmEndLesson,
  };
};
